import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { treatExperiment } from '#src/app/shared/api/experiments';
import { isMobile } from '#src/app/shared/utils/userAgent';

import type { PetitionDetailsDataExperimentsQuery } from '../experiments.graphql';

type Experiment = PetitionDetailsDataExperimentsQuery['experiments']['petitionDetailsShareCtaPlacementExperiment'];

type Params = {
	experiment: Experiment;
	utilityContext: UtilityContext;
};

/**
 * Unlike its sibling normalizers in this directory (which return only `Experiment['variation']`),
 * this one returns the full, `treated`-corrected experiment object. `MobileSignButtonContainer`
 * passes it into `<TrackExperimentExposure>`, which calls `useTrackExperimentExposureOnMount` and
 * needs `treated`/`fallback` to decide whether to fire `$exposure` — narrowing this to a bare
 * variation string would silently break that.
 */
export async function normalizePetitionDetailsShareCtaPlacementExperiment({
	experiment,
	utilityContext,
}: Params): Promise<Experiment> {
	if (!shouldRunExperiment({ experiment, utilityContext })) {
		// Experiment Service bucketing is keyed on UUID alone, so `experiment.variation`/`treated` can
		// already reflect a treatment arm here even though this user shouldn't see one (non-mobile or FC
		// disabled) — e.g. a user treated on a prior mobile visit revisiting on desktop. Force `control`
		// so the rendered UI respects the gate, and force `treated: false` so `useTrackExperimentExposure`
		// doesn't fire a control `$exposure` for this out-of-scope visit.
		return { ...experiment, variation: 'control', treated: false };
	}

	if (experiment.override) {
		// A QA/dev `?exo.` override already forced `variation`/`override: true` upstream, and
		// `useTrackExperimentExposure` already no-ops `$exposure` for `override`. Pass it through
		// unchanged instead of calling `treatExperiment`: that hits the real Experiment Service treat
		// endpoint for the session UUID, which would register a real bucket assignment for a visit
		// that was never a genuine assignment — polluting `experiment_variations` for that user.
		return experiment;
	}

	await treatExperiment(utilityContext, experiment.experimentName);

	return { ...experiment, treated: true };
}

/**
 * Uses `isMobile()` (matches both `mobile` and `tablet` device types), same as the sibling
 * normalizers. Tablets sit at >=768px and get the *desktop* `md:` layout — the in-flow/sticky
 * `MobileActionButtonContainer` tree is `display: none` for them — but tablets are still counted as
 * part of the mobile population for this experiment's bucketing/exposure.
 */
function shouldRunExperiment({ experiment, utilityContext }: Params): boolean {
	return isEnabledByFC(experiment) && isMobile(utilityContext);
}

/**
 * This experiment uses a static `.withExperimentName(...)`, so `enabledIf` failing (and any other
 * resolution fallback — fetch errors, the experiment not being registered yet, etc.) surfaces as
 * `fallback: true` on the resolved experiment (see `createExperimentsProvider.ts`'s
 * `getVariationForExperimentService` in the middle-end) — that's the signal to check instead.
 */
function isEnabledByFC(experiment: Experiment): boolean {
	return !experiment.fallback;
}
